
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { BarChart4, Globe, Users, Zap, Briefcase, CreditCard } from 'lucide-react';

const WhyChooseUs = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const reasons = [
    {
      icon: <BarChart4 size={36} className="text-blue-500" />,
      title: '1500+ Successful Projects',
      description: 'Track record of delivering excellence across industries',
    },
    {
      icon: <Users size={36} className="text-blue-500" />,
      title: '15+ Energetic Experts',
      description: 'Skilled professionals dedicated to your success',
    },
    {
      icon: <Briefcase size={36} className="text-blue-500" />,
      title: 'Client-Focused Approach',
      description: 'Your goals and vision guide our every decision',
    },
    {
      icon: <Globe size={36} className="text-blue-500" />,
      title: 'Global & Local Reach',
      description: 'Serving clients worldwide with local expertise',
    },
    {
      icon: <Zap size={36} className="text-blue-500" />,
      title: 'Cutting-edge Tech',
      description: 'Using the latest tools and technologies for optimal results',
    },
    {
      icon: <CreditCard size={36} className="text-blue-500" />,
      title: 'Budget-Friendly Solutions',
      description: 'Quality services tailored to fit your budget',
    },
  ];

  return (
    <section className="py-20 relative overflow-hidden" id="why-choose-us">
      {/* Animated background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 opacity-5">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-blue-500"
              style={{
                width: Math.random() * 20 + 5,
                height: Math.random() * 20 + 5,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, Math.random() * -100, 0],
                x: [0, Math.random() * 50 - 25, 0],
              }}
              transition={{
                duration: Math.random() * 10 + 20,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="section-heading text-4xl md:text-5xl font-bold mb-4"
          >
            Why Choose <span className="text-blue-500">DG IT</span>?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-600 max-w-2xl mx-auto"
          >
            We combine creativity, expertise and dedication to deliver exceptional results for your business
          </motion.p>
        </div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300"
              whileHover={{
                scale: 1.03,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              }}
            >
              <div className="mb-4 p-3 bg-blue-50 rounded-full inline-block">
                {reason.icon}
              </div>
              <h3 className="text-xl font-bold mb-2">{reason.title}</h3>
              <p className="text-gray-600">{reason.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {inView && (
          <div className="mt-16 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <a 
                href="#contact" 
                className="px-8 py-3 inline-block text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                style={{ 
                  background: 'linear-gradient(135deg, #0052CC, #00C6FF)',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #003C99, #0099CC)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 198, 255, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #0052CC, #00C6FF)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                }}
              >
                Start Your Project Today
              </a>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};

export default WhyChooseUs;
